import { Avatar, AvatarFallback, AvatarImage, AvatarInitials, type AvatarSize } from "@/components/Avatar";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { Text } from "@/components/podkit/typography/Text";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { useMembers } from "@/queries/organization-queries";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { type PlainMessage } from "@bufbuild/protobuf";
import { useMemo, type FC } from "react";

type SubjectDisplayProps = {
    subject?: PlainMessage<Subject>;
    avatarSize?: AvatarSize;
};

/**
 * Displays a Subject (user or service account) with their avatar and name.
 * Resolves the subject ID to display name by looking up organization members
 * and service accounts.
 */
export const SubjectDisplay: FC<SubjectDisplayProps> = ({ subject, avatarSize = 16 }) => {
    const { data: serviceAccountsData } = useListServiceAccounts();
    const { data: membersData } = useMembers();

    const userInfo = useMemo(() => {
        if (!subject) {
            return null;
        }

        if (subject.principal === Principal.USER) {
            const member = membersData?.members?.find((m) => m.userId === subject.id);
            if (member) {
                return {
                    name: member.fullName,
                    avatarUrl: member.avatarUrl,
                    principal: Principal.USER,
                };
            }
        }

        if (subject.principal === Principal.SERVICE_ACCOUNT) {
            const serviceAccount = serviceAccountsData?.serviceAccounts?.find((sa) => sa.id === subject.id);
            if (serviceAccount) {
                return {
                    name: serviceAccount.name,
                    principal: Principal.SERVICE_ACCOUNT,
                    id: serviceAccount.id,
                };
            }
        }

        return null;
    }, [subject, membersData, serviceAccountsData]);

    if (!userInfo) {
        return null;
    }

    return (
        <div className="flex items-center gap-1.5">
            {userInfo.principal === Principal.USER ? (
                <Avatar size={avatarSize}>
                    {userInfo.avatarUrl && <AvatarImage src={userInfo.avatarUrl} alt={`${userInfo.name}'s avatar`} />}
                    <AvatarFallback>
                        <AvatarInitials name={userInfo.name} size={avatarSize} />
                    </AvatarFallback>
                </Avatar>
            ) : userInfo.id ? (
                <ServiceAccountAvatar id={userInfo.id} size={avatarSize} />
            ) : null}
            <Text className="text-sm">{userInfo.name}</Text>
        </div>
    );
};
